import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

/**
 * Enrichment flags - bitfield for composable enrichment requests
 * Must match backend src/types/enrichment.ts
 */
const EnrichmentFlags = {
	NONE: 0b0000, // 0  - Fully enriched
	TAGS: 0b0001, // 1  - Regenerate tags from existing metadata
	AI: 0b0010, // 2  - Run AI classification/categorization
	METADATA: 0b0100, // 4  - Fetch metadata from external source
	FORCE: 0b1000, // 8  - Skip cache, force fresh fetch
} as const;

/**
 * Available enrichers for progressive enrichment.
 */
const ENRICHERS = [
	{ name: 'reddit', label: 'Reddit', description: 'Fetch Reddit user/subreddit data' },
	{ name: 'instagram', label: 'Instagram', description: 'Fetch Instagram posts/profiles' },
	{ name: 'youtube', label: 'YouTube', description: 'Fetch YouTube video metadata' },
	{ name: 'pornhub', label: 'PornHub', description: 'Fetch PornHub video data' },
	{ name: 'webpage', label: 'WebPage', description: 'Fallback OG/meta tag extraction' },
	{ name: 'openrouter', label: 'OpenRouter AI', description: 'AI-powered categorization' },
	{ name: 'secondary_tags', label: 'Secondary Tags', description: 'Generate tags from metadata' },
] as const;

interface EnrichOptionsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (flags: number, enrichers?: string[]) => void;
	showEnricherSelection?: boolean;
}

export function EnrichOptionsModal({
	open,
	onOpenChange,
	onSubmit,
	showEnricherSelection = false,
}: EnrichOptionsModalProps) {
	const [metadata, setMetadata] = useState(true);
	const [tags, setTags] = useState(true);
	const [ai, setAi] = useState(false);
	const [force, setForce] = useState(false);

	// Enricher selection (for progressive enrichment)
	const [showEnrichers, setShowEnrichers] = useState(false);
	const [selectedEnrichers, setSelectedEnrichers] = useState<string[]>(
		ENRICHERS.map((e) => e.name)
	);

	function toggleEnricher(name: string) {
		setSelectedEnrichers((prev) =>
			prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
		);
	}

	function selectAllEnrichers() {
		setSelectedEnrichers(ENRICHERS.map((e) => e.name));
	}

	function deselectAllEnrichers() {
		setSelectedEnrichers([]);
	}

	function handleSubmit() {
		let flags = 0;
		if (metadata) flags |= EnrichmentFlags.METADATA;
		if (tags) flags |= EnrichmentFlags.TAGS;
		if (ai) flags |= EnrichmentFlags.AI;
		if (force) flags |= EnrichmentFlags.FORCE;

		// Pass enricher selection only if expanded and not all selected
		const enrichers =
			showEnricherSelection && showEnrichers && selectedEnrichers.length < ENRICHERS.length
				? selectedEnrichers
				: undefined;

		onSubmit(flags, enrichers);
		onOpenChange(false);
	}

	const currentFlags =
		(metadata ? EnrichmentFlags.METADATA : 0) |
		(tags ? EnrichmentFlags.TAGS : 0) |
		(ai ? EnrichmentFlags.AI : 0) |
		(force ? EnrichmentFlags.FORCE : 0);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Enrichment Options</DialogTitle>
					<DialogDescription>
						Select which enrichment operations to run. SHIFT+click the Enrich button to open this
						dialog.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<label className="flex items-start gap-3 cursor-pointer group">
						<input
							type="checkbox"
							checked={metadata}
							onChange={(e) => setMetadata(e.target.checked)}
							className="mt-1 size-4 rounded border-gray-300 text-primary focus:ring-primary"
						/>
						<div>
							<div className="font-medium group-hover:text-primary transition-colors">
								Fetch Metadata
							</div>
							<div className="text-sm text-muted-foreground">
								Retrieve title, description, thumbnail, and data from external source
							</div>
						</div>
					</label>

					<label className="flex items-start gap-3 cursor-pointer group">
						<input
							type="checkbox"
							checked={tags}
							onChange={(e) => setTags(e.target.checked)}
							className="mt-1 size-4 rounded border-gray-300 text-primary focus:ring-primary"
						/>
						<div>
							<div className="font-medium group-hover:text-primary transition-colors">
								Generate Tags
							</div>
							<div className="text-sm text-muted-foreground">
								Auto-generate tags from URL and metadata (domain, platform, users)
							</div>
						</div>
					</label>

					<label className="flex items-start gap-3 cursor-pointer group">
						<input
							type="checkbox"
							checked={ai}
							onChange={(e) => setAi(e.target.checked)}
							className="mt-1 size-4 rounded border-gray-300 text-primary focus:ring-primary"
						/>
						<div>
							<div className="font-medium group-hover:text-primary transition-colors">
								AI Classification
							</div>
							<div className="text-sm text-muted-foreground">
								Use AI to suggest category based on content analysis
							</div>
						</div>
					</label>

					<hr className="my-2 border-border" />

					<label className="flex items-start gap-3 cursor-pointer group">
						<input
							type="checkbox"
							checked={force}
							onChange={(e) => setForce(e.target.checked)}
							className="mt-1 size-4 rounded border-gray-300 text-primary focus:ring-primary"
						/>
						<div>
							<div className="font-medium group-hover:text-primary transition-colors">
								Force Refresh
							</div>
							<div className="text-sm text-muted-foreground">
								Skip cache and fetch everything fresh from source
							</div>
						</div>
					</label>

					{/* Enricher Selection (Progressive Enrichment) */}
					{showEnricherSelection && (
						<>
							<hr className="my-2 border-border" />
							<button
								type="button"
								onClick={() => setShowEnrichers(!showEnrichers)}
								className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors w-full"
							>
								{showEnrichers ? (
									<ChevronUp className="size-4" />
								) : (
									<ChevronDown className="size-4" />
								)}
								Advanced: Select Enrichers
								<span className="text-muted-foreground font-normal">
									({selectedEnrichers.length}/{ENRICHERS.length})
								</span>
							</button>

							{showEnrichers && (
								<div className="space-y-3 pl-6 border-l-2 border-border">
									<div className="flex gap-2">
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={selectAllEnrichers}
											className="h-7 text-xs"
										>
											Select All
										</Button>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={deselectAllEnrichers}
											className="h-7 text-xs"
										>
											Deselect All
										</Button>
									</div>
									{ENRICHERS.map((enricher) => (
										<label
											key={enricher.name}
											className="flex items-start gap-3 cursor-pointer group"
										>
											<input
												type="checkbox"
												checked={selectedEnrichers.includes(enricher.name)}
												onChange={() => toggleEnricher(enricher.name)}
												className="mt-0.5 size-4 rounded border-gray-300 text-primary focus:ring-primary"
											/>
											<div>
												<div className="text-sm font-medium group-hover:text-primary transition-colors">
													{enricher.label}
												</div>
												<div className="text-xs text-muted-foreground">
													{enricher.description}
												</div>
											</div>
										</label>
									))}
								</div>
							)}
						</>
					)}
				</div>

				<DialogFooter className="flex-row justify-between items-center sm:justify-between">
					<span className="text-xs text-muted-foreground font-mono">
						flags: {currentFlags} (0b{currentFlags.toString(2).padStart(4, '0')})
					</span>
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button onClick={handleSubmit} disabled={currentFlags === 0}>
							Enrich
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
